import { Link } from 'react-router-dom';

import { LogoBadge } from 'components';
import { PATHS } from 'routes/routePaths';
import ProfileMenu from './ProfileMenu';
import ThemeToggle from './ThemeToggle';
import { MenuIcon } from './icons';

/**
 * Navbar — sticky top bar for authenticated areas. Left: a mobile menu button
 * (opens the drawer) + brand. Right: the theme toggle and the profile dropdown
 * (which carries the logout action). Clean white bar with a soft hairline.
 *
 * The notification bell is deliberately absent. It shipped rendering three
 * hardcoded sample notifications — "Welcome to LearnX", "Complete your
 * profile" — to every student, mentor and admin, under a pulsing unread dot
 * that never cleared. A permanently-wrong badge in the most prominent control
 * in the shell teaches people to ignore the one channel we'd want to reach them
 * through, so it is better absent than lying. `NotificationBell.js` is kept for
 * its markup; re-mount it here once it reads the real per-role action feed.
 *
 * @param {Function} onOpenMobile  Open the responsive drawer (mobile only).
 */
export default function Navbar({ onOpenMobile }) {
  return (
    <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-4 sm:px-6">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onOpenMobile}
          aria-label="Open navigation"
          className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 md:hidden"
        >
          <MenuIcon aria-hidden />
        </button>

        <Link to={PATHS.LANDING} aria-label="LearnX home" className="rounded-xl focus-ring">
          <LogoBadge size="md" />
        </Link>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2">
        <ThemeToggle />
        <span className="mx-1 hidden h-6 w-px bg-slate-200 sm:block" />
        <ProfileMenu />
      </div>
    </header>
  );
}
